/**
 * 權限門控組件
 * 根據用戶權限決定是否顯示內容
 */

import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Alert } from 'react-bootstrap';
import { isSuperAdmin } from '../utils/superAdmin';
import { MEMBER_TERM } from '../config/clubTerms';

// 用戶角色判斷
function getUserRole(user) {
  if (!user) return 'guest';
  
  const { e_id, e_position } = user;
  
  if (isSuperAdmin(e_id)) return 'super_admin';
  if (parseInt(e_position) === 17) return 'secretary';
  return 'member';
}

// 權限檢查
function hasRole(user, requiredRole) {
  const userRole = getUserRole(user);
  const levels = {
    guest: 0,
    member: 1,
    secretary: 2,
    super_admin: 3
  };
  
  return levels[userRole] >= levels[requiredRole];
}

/**
 * 權限門控組件
 * @param {Object} props
 * @param {string} props.role - 所需的最低權限角色
 * @param {React.ReactNode} props.children - 需要保護的內容
 * @param {React.ReactNode} props.fallback - 權限不足時顯示的內容
 * @param {boolean} props.showError - 是否顯示權限不足錯誤訊息
 */
export const PermissionGate = ({ 
  role, 
  children, 
  fallback = null, 
  showError = false 
}) => {
  const { user } = useAuth();
  
  if (hasRole(user, role)) {
    return children;
  }
  
  if (showError) {
    return (
      <Alert variant="warning">
        <Alert.Heading>權限不足</Alert.Heading>
        <p>您需要 <strong>{getRoleDisplayName(role)}</strong> 權限才能查看此內容。</p>
        <p>目前權限：<strong>{getRoleDisplayName(getUserRole(user))}</strong></p>
      </Alert>
    );
  }
  
  return fallback;
};

/**
 * 條件渲染 Hook
 * @param {string} requiredRole - 所需權限
 * @returns {boolean} 是否有權限
 */
export function usePermission(requiredRole) {
  const { user } = useAuth();
  return hasRole(user, requiredRole);
}

/**
 * 多權限檢查 Hook
 * @param {string[]} roles - 權限列表
 * @returns {Object} 各種權限狀態
 */
export function usePermissions(roles = []) {
  const { user } = useAuth();
  const userRole = getUserRole(user);
  
  const permissions = {};
  roles.forEach(role => {
    permissions[role] = hasRole(user, role);
  });
  
  return {
    userRole,
    permissions,
    isSuperAdmin: userRole === 'super_admin',
    isSecretary: userRole === 'secretary' || userRole === 'super_admin',
    isMember: userRole !== 'guest',
    isGuest: userRole === 'guest'
  };
}

// 角色顯示名稱
function getRoleDisplayName(role) {
  const names = {
    guest: '訪客',
    member: MEMBER_TERM,
    secretary: '秘書',
    super_admin: '超級管理員'
  };
  return names[role] || '未知';
}

/**
 * 角色標籤組件
 */
export const RoleBadge = ({ user, showDetails = false }) => {
  const role = getUserRole(user);
  const displayName = getRoleDisplayName(role);
  
  const variants = {
    guest: 'secondary',
    member: 'primary',
    secretary: 'success',
    super_admin: 'danger'
  };
  
  return (
    <span className={`badge bg-${variants[role] || 'secondary'}`}>
      {displayName}
      {showDetails && user && (
        <small className="ms-1">
          ({user.e_name})
        </small>
      )}
    </span>
  );
};

/**
 * 權限感知的按鈕組件
 */
export const PermissionButton = ({ 
  role, 
  children, 
  disabled = false,
  tooltip,
  ...buttonProps 
}) => {
  const { user } = useAuth();
  const hasPermission = hasRole(user, role);
  
  return (
    <button
      {...buttonProps}
      disabled={disabled || !hasPermission}
      title={!hasPermission ? `需要${getRoleDisplayName(role)}權限` : tooltip}
      className={`${buttonProps.className || ''} ${!hasPermission ? 'opacity-50' : ''}`}
    >
      {children}
    </button>
  );
};

/**
 * 權限感知的導航項目
 */
export const PermissionNavItem = ({ 
  role, 
  children, 
  href, 
  className = '' 
}) => {
  const hasPermission = usePermission(role);
  
  if (!hasPermission) {
    return null;
  }
  
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
};

export default PermissionGate;